import { useEffect, useState } from 'react';
import { LoaderCircle, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useStoreSettings, useUpdateStoreSettings } from '@/api/hooks/useSettings';
import type { PaymentMode, StoreSettings } from '@/api/types';
import { useSession } from '@/auth/session';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';

const PAYMENT_MODES: PaymentMode[] = ['Cash', 'Upi', 'Card', 'Credit', 'Online'];

function toFormState(settings: StoreSettings) {
  return {
    storeName: settings.storeName,
    storeAddr: settings.storeAddr,
    storePh: settings.storePh,
    gstin: settings.gstin,
    dlNo: settings.dlNo,
    footer: settings.footer,
    invoicePrefix: settings.invoicePrefix,
    defaultGst: String(settings.defaultGst),
    defaultDiscountPct: String(settings.defaultDiscountPct),
    defaultPaymentMode: settings.defaultPaymentMode,
    roundOffBills: settings.roundOffBills,
    requireCustomerPhone: settings.requireCustomerPhone,
    autoPrintAfterBill: settings.autoPrintAfterBill,
    auditTrailEnabled: settings.auditTrailEnabled,
    gstEnabled: settings.gstEnabled,
  };
}

export function StoreSettingsCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const canEdit = can(PERMISSIONS.settingsEdit);

  const { data, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();

  const [form, setForm] = useState<ReturnType<typeof toFormState> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setForm(toFormState(data));
  }, [data]);

  function set<K extends keyof NonNullable<typeof form>>(key: K, value: NonNullable<typeof form>[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function handleSave() {
    if (!form) return;
    setError(null);

    try {
      await updateSettings.mutateAsync({
        storeName: form.storeName.trim(),
        storeAddr: form.storeAddr.trim(),
        storePh: form.storePh.trim(),
        gstin: form.gstin.trim(),
        dlNo: form.dlNo.trim(),
        footer: form.footer.trim(),
        invoicePrefix: form.invoicePrefix.trim(),
        defaultGst: Number(form.defaultGst) || 0,
        defaultDiscountPct: Number(form.defaultDiscountPct) || 0,
        defaultPaymentMode: form.defaultPaymentMode,
        roundOffBills: form.roundOffBills,
        requireCustomerPhone: form.requireCustomerPhone,
        autoPrintAfterBill: form.autoPrintAfterBill,
        auditTrailEnabled: form.auditTrailEnabled,
        gstEnabled: form.gstEnabled,
      });
      toast({ title: 'Store settings saved', variant: 'success' });
    } catch (err) {
      // PREFIX_IN_USE is the one a pharmacist can act on — surface it plainly
      // rather than a generic failure toast.
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (isLoading || !form) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Store className="h-4 w-4" /> Store details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <fieldset disabled={!canEdit} className="space-y-4 disabled:opacity-60">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-name">Store name</Label>
              <Input id="s-name" value={form.storeName} onChange={(e) => set('storeName', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-phone">{t('phone')}</Label>
              <Input id="s-phone" value={form.storePh} onChange={(e) => set('storePh', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="s-addr">{t('address')}</Label>
            <Input id="s-addr" value={form.storeAddr} onChange={(e) => set('storeAddr', e.target.value)} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-gstin">GSTIN</Label>
              <Input id="s-gstin" value={form.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} maxLength={15} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-dl">Drug licence no.</Label>
              <Input id="s-dl" value={form.dlNo} onChange={(e) => set('dlNo', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="s-footer">Bill footer message</Label>
            <Input id="s-footer" value={form.footer} onChange={(e) => set('footer', e.target.value)} maxLength={300} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="s-prefix">Invoice prefix</Label>
              <Input id="s-prefix" value={form.invoicePrefix} onChange={(e) => set('invoicePrefix', e.target.value)} maxLength={12} />
              <p className="text-xs text-muted-foreground">Changing this starts a new number sequence.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-gst">Default GST % for new medicines</Label>
              <Input id="s-gst" type="number" min={0} max={100} value={form.defaultGst} onChange={(e) => set('defaultGst', e.target.value)} />
              <p className="text-xs text-muted-foreground">
                The GST rate most of your medicines use. Only pre-fills the GST field when you add a new
                medicine — it never changes what's billed. Each medicine keeps its own GST %, editable any
                time from Inventory.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-disc">Default discount %</Label>
              <Input id="s-disc" type="number" min={0} max={100} value={form.defaultDiscountPct} onChange={(e) => set('defaultDiscountPct', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Default payment mode</Label>
            <Select value={form.defaultPaymentMode} onValueChange={(v) => set('defaultPaymentMode', v as PaymentMode)}>
              <SelectTrigger className="sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {mode}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 rounded-md bg-secondary/50 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Round off bills</p>
                <p className="text-xs text-muted-foreground">Round the total to the nearest rupee.</p>
              </div>
              <Switch checked={form.roundOffBills} onCheckedChange={(v) => set('roundOffBills', v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Require customer phone</p>
                <p className="text-xs text-muted-foreground">Block checkout without a phone number.</p>
              </div>
              <Switch checked={form.requireCustomerPhone} onCheckedChange={(v) => set('requireCustomerPhone', v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Auto-print after billing</p>
                <p className="text-xs text-muted-foreground">Open the bill automatically after checkout.</p>
              </div>
              <Switch checked={form.autoPrintAfterBill} onCheckedChange={(v) => set('autoPrintAfterBill', v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Charge GST on bills</p>
                <p className="text-xs text-muted-foreground">
                  On by default for a GST-registered pharmacy. Turn off only if you're genuinely below the
                  registration threshold — every bill's GST becomes ₹0 regardless of each medicine's own rate,
                  since an unregistered dealer can't legally collect GST on anything. Each medicine's configured
                  rate is preserved for whenever you turn this back on.
                </p>
              </div>
              <Switch checked={form.gstEnabled} onCheckedChange={(v) => set('gstEnabled', v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Audit trail</p>
                <p className="text-xs text-muted-foreground">
                  Log every staff action (who changed what, when). Off by default; only an admin can turn this on.
                </p>
              </div>
              <Switch
                checked={form.auditTrailEnabled}
                onCheckedChange={(v) => set('auditTrailEnabled', v)}
              />
            </div>
          </div>
        </fieldset>

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        {canEdit ? (
          <Button onClick={handleSave} disabled={updateSettings.isPending}>
            {updateSettings.isPending && <LoaderCircle className="animate-spin" />}
            Save store settings
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">You have view-only access to store settings.</p>
        )}
      </CardContent>
    </Card>
  );
}
